import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { getInspectionUrl, formCategories } from "@Helpers";
import { formCategoryObject } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import { taskInspectionAndProgressFormsController, getTemplates, getTemplateTypes } from "@Actions/Forms";

import axiosWrapper from "@AxiosWrapper";
import type {
  FormFlowControllerResponse,
  TaskInspectionAndProgressFormsControllerRequest,
  TaskInspectionAndProgressFormsControllerResponse,
  TipfSaveRequest,
} from "@ServerTypes/forms/forms.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { FormFlowLevel, FormFlowStep } from "../FormFlow/formFlow.types";
import _ from "lodash";

/**
 * One row of the flattened template tree the wizard's `TreeSelectDropDown` renders — either a form
 * TYPE (a parent, `parentId: null`) or a TEMPLATE beneath it, both produced by `combineArrays` below.
 *
 * SERVER GAP: `getTemplatesResponse` and `getTemplateTypesResponse` both declare their payload as
 * `result: z.array(z.any())`, so there is no server type to import. Only the members this hook and
 * `InspectionCreation` read are named; delete this once those two responses are modelled.
 */
export interface TemplateTreeRow {
  _id?: ObjectIdString;
  name?: string;
  resourceType?: string;
  type?: { _id?: ObjectIdString; name?: string };
  project?: { noOfFormsOfThisTemplate?: number };
  /** Tree bookkeeping added by `combineArrays`, not stored on the template. */
  isChild?: boolean;
  hasChild?: boolean;
  parentId?: ObjectIdString | null;
  typeName?: string;
}

/**
 * A reusable form-flow template as the wizard receives it. Same SERVER GAP as above:
 * `formFlowControllerResponse.result` is `z.array(z.any())`, so only the members read here are named.
 */
export interface FormFlowOption {
  _id?: ObjectIdString;
  name?: string;
  formFlow?: FormFlowLevel[];
  selectedForm?: { _id?: ObjectIdString };
  /**
   * These rows go straight into `SelectDropDown`, whose option constraint is
   * `Record<string, unknown>`; and they come off a `z.array(z.any())` payload, so the rest of the
   * document really is arbitrary. Both facts are the same index signature.
   */
  [key: string]: unknown;
}

/**
 * The three list payloads this hook reads — `getTemplateTypesResponse.result`,
 * `getTemplatesResponse.result` and `formFlowControllerResponse.result` — are all
 * `z.array(z.unknown())` on the server, so their rows arrive as `unknown` and have to be narrowed
 * before they can be treated as the row shapes above. That `z.unknown()` is deliberate, not sloppy:
 * the element is a whole `Forms_types` / `Template` / `formFlow` document, and the collection schema
 * that already models it imports forms.schema, so restating the element there would close a module
 * cycle (the comment above `getTemplatesResponse` says exactly this).
 *
 * What is checked is what the rows have to be for the readers below: a JSON-round-tripped Mongo
 * document — an object, not an array, whose `_id` (used as the tree's `Map` key and as the dropdown
 * value) is the hex string Mongo serialises an `ObjectId` to. The remaining members are optional on
 * both row types, so nothing that is actually returned is dropped; a row that fails this could only
 * come from a response that is not the documented one, and such a row produces nothing usable today
 * either (`combineArrays` dereferences `template.type._id` on it).
 */
const isDocumentRow = (value: unknown): boolean =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  (!("_id" in value) || typeof value._id === "string");

const isTemplateTreeRow = (value: unknown): value is TemplateTreeRow => isDocumentRow(value);

const isFormFlowOption = (value: unknown): value is FormFlowOption => isDocumentRow(value);

/** The two lists the flow dropdown renders: every flow, and what the "I'm part of" filter kept. */
interface FormFlowOptionLists {
  allOptions: FormFlowOption[];
  filteredOptions: FormFlowOption[];
}

/**
 * The contract "based on" step. `name` is the entity currently being picked (`"budget"`, `"tasks"`,
 * `"milestones"`, a `formCategories` value, or `""` once the pick is committed); the other keys hold
 * the ids picked for an entity, stored under that same name. The index signature is not optional on
 * purpose — the component reads `attachEntity[category]?.length > 0`, which a `| undefined` element
 * type would reject.
 */
type AttachEntityState = { name: string } & { [entity: string]: string | ObjectIdString[] };

/**
 * The save response as this hook reads it.
 *
 * SERVER GAP: `taskInspectionAndProgressFormsControllerResponse` declares only
 * `{ status, errorMessage?, result?, count?, lastSaved? }`, but the `save` path also returns the new
 * instance's `id`, which the redirect below depends on. Declared here so that read type-checks
 * without `any`; delete once the field is added to `packages/server/forms/forms.schema.ts`.
 */
type SaveFormResponseData = TaskInspectionAndProgressFormsControllerResponse & { id?: ObjectIdString };

interface UseCreateInspectionProps {
  /**
   * Space the form is created in; `null` until a contract's space resolves. Unbranded, because the
   * component that supplies it reads it off a route param / redux slice that has no brand; the two
   * places the request schema wants a branded id assert it there instead.
   */
  spaceId?: string | null;
  projectId?: string;
  contractId?: string;
  /** A `formCategories` value or a custom module's `resourceType`. */
  resourceType?: string;
  portalData?: RootState["portalData"];
  setShowFormFlowCreationModal: (state: { status: boolean; category: string }) => void;
  navigate?: (to: string) => void;
  getContractDetails?: typeof import("@Features/contracts/state/contracts.actions").getContractDetails;
  mobile?: boolean;
  /** Mobile only: the bottom sheet the wizard lives in, dismissed after a successful create. */
  createInspectionRef?: { current?: { dismiss: () => void } | null };
  /** Mobile only: the navigator the created form is pushed onto. */
  router?: { push: (screen: string, params: Record<string, unknown>) => void };
  /**
   * Accepted but not read here. `InspectionCreation` spreads its own props into this call, so these
   * arrive whether or not the hook wants them; declaring them keeps that spread honest.
   */
  showFormFlowCreationModal?: { status: boolean; category: string };
  source?: string;
  formCategoryObject?: unknown;
  getInspectionUrl?: unknown;
  setChangeOrderState?: unknown;
}

const useCreateInspection = (props: UseCreateInspectionProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [title, setTitle] = useState("");
  // Epoch ms (the picker hands over `new Date(...).setHours(...)`), or `null` for "no due date".
  const [dueDate, setDueDate] = useState<number | null>(null);
  const [description, setDescription] = useState("");
  const [selectedFormTypeId, setSelectedFormTypeId] = useState("");
  const [selectedForm, setSelectedForm] = useState<TemplateTreeRow>({});
  const [rememberUserSettings, setRememberUserSettings] = useState(false);
  const [templateDataSource, setTemplateDataSource] = useState<TemplateTreeRow[]>([]);
  // Which of the two preview modals is open, if either.
  const [showPreview, setShowPreview] = useState<"Template" | "FormFlow" | null>(null);
  const [showCreateFlowModal, setShowCreateFlowModal] = useState(false);
  // `| undefined` is real, not defensive: `handleCancelFormAttachment` resets this straight back to
  // `props.spaceId`, which is optional. The type argument is erased at emit.
  const [spaceId, setSpaceId] = useState<string | null | undefined>(props?.spaceId || null);
  const [selectedFormFlow, setSelectedFormFlow] = useState<FormFlowOption>({});
  const [attachEntity, setAttachEntity] = useState<AttachEntityState>({ name: "budget" });
  const [formFlowOptions, setFormFlowOptions] = useState<FormFlowOptionLists>({
    allOptions: [],
    filteredOptions: [],
  });
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isFormFlowLoading, setIsFormFlowLoading] = useState(false);
  const [formFlowFilter, setFormFlowFilter] = useState<string>("");

  const isRustomjeePortal = props.portalData?.frontendUrl?.includes("rustomjee") || false;
  // Both assertions are erased at emit: `frontendUrl` and `resourceType` are optional on their own
  // types, and `Array.prototype.includes` wants the element type, so an absent value simply misses —
  // exactly as it does today. Guarding instead would change the emitted comparison.
  const isMPMRCLPortal =
    ["mpmrcl.app.cube.ms", "cube-pmis.mpmrcl.in"].includes(props.portalData?.frontendUrl!) || false;
  const progressUpdateEntityCondition =
    [formCategories.inspection, formCategories.bills].includes(props.resourceType!) && !isRustomjeePortal;
  const inspectionEntityCondition = props.resourceType == formCategories.bills;
  const allowedAttachmentConstants = [formCategories.progressUpdates, formCategories.inspection];

  const contractDetails = useSelector((state: RootState) => state.contractDetails);

  const customModules = useSelector((state: RootState) => state.customModules);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  useEffect(() => {
    if (props.contractId) {
      setSpaceId(_.get(contractDetails, "space._id"));
    }
  }, [contractDetails, props.contractId]);

  useEffect(() => {
    const fetchFormFlows = async () => {
      if (selectedForm?._id && spaceId && props.projectId && !showCreateFlowModal) {
        setIsFormFlowLoading(true);
        try {
          const response = await axiosWrapper.post<FormFlowControllerResponse>(
            `${getBackendUrl()}/forms/formFlowController`,
            {
              value: { selectedFormId: selectedForm._id, spaceId, projectId: props.projectId, isArchived: false },
            },
          );
          const flowRows = response.data?.result;
          if (flowRows) {
            // Narrowed, not asserted — see `isFormFlowOption`. This is the list read path
            // (`selectedFormId`, no `_id`), which returns `formFlow` documents straight off a
            // `.toArray()`, so every row passes and the two lists are the payload unchanged.
            const flowOptions = flowRows.filter(isFormFlowOption);
            setFormFlowOptions({
              allOptions: flowOptions,
              filteredOptions: flowOptions,
            });
            if (flowOptions.length === 1) {
              // Annotated so the `{}` fallback is contextually typed as the option rather than as
              // the empty type; the `||` and the value it selects are unchanged.
              const onlyFlow: FormFlowOption = flowOptions[0] || {};
              setSelectedFormFlow(onlyFlow);
            } else {
              setSelectedFormFlow({});
            }
          }
        } catch (error) {
          console.error("Error fetching form flows:", error);
          Promise.reject(error);
        } finally {
          setIsFormFlowLoading(false);
        }
      }
    };
    fetchFormFlows();
  }, [selectedForm?._id, spaceId, props.projectId, showCreateFlowModal]);

  const handleCancelFormAttachment = () => {
    setTitle("");
    setDescription("");
    setDueDate(null);
    setSpaceId(props.spaceId);
    props.setShowFormFlowCreationModal({ status: false, category: "" });
  };
  const combineArrays = useCallback(
    (typeArray: TemplateTreeRow[] | undefined, templateArray: TemplateTreeRow[] | undefined) => {
      // The tuple annotation is what makes this a `Map` entry list rather than an array of arrays; it is
      // a type annotation only, erased at emit.
      const typeMap = new Map(
        (typeArray ?? []).map((type): [ObjectIdString | undefined, TemplateTreeRow] => [
          type._id,
          {
            _id: type._id,
            isChild: false,
            name: type.name,
            parentId: null,
          },
        ]),
      );

      const combinedArray: TemplateTreeRow[] = [];

      (templateArray ?? []).forEach((template) => {
        // KNOWN BUG, preserved verbatim: `type` is optional on the row, but it is dereferenced
        // unconditionally, so a template stored without one throws. Asserted (erased at emit); the fix
        // is a guard plus a skip, which is a behaviour change.
        const parentType: TemplateTreeRow | undefined = typeMap.get(template.type!._id);

        // KNOWN BUG, preserved verbatim: when `parentType` is absent this stores `{ hasChild: true }`
        // with no `_id` and no `name`, and the `.filter` at the end keeps it (because `hasChild` is
        // truthy) — so an id-less, name-less row reaches the template tree. Left exactly as it is.
        typeMap.set(template.type!._id, { ...(parentType || {}), hasChild: true });

        if (parentType) {
          combinedArray.push({
            ...template,
            isChild: true,
            typeName: parentType.name,
            parentId: parentType._id,
            hasChild: false,
          });
        }
      });

      const valuesArray = Array.from(typeMap.values());

      valuesArray?.forEach((type) => {
        combinedArray.push({ ...type, parentId: null });
      });

      return combinedArray.filter((data) => data.parentId || data.hasChild);
    },
    [],
  );
  const fetchTemplateDataSource = useCallback(async () => {
    setIsTemplateLoading(true);
    try {
      const templateTypes = await getTemplateTypes({
        value: {
          resourceType: props.resourceType,
        },
      });
      const templates = await getTemplates({
        value: {
          // The project id arrives unbranded from the route param; the request schema wants the branded
          // 24-hex form. Erased at emit.
          projectId: props.projectId as ObjectIdString,
          resourceType: props.resourceType,
          isArchived: false,
        },
      });

      // Both payloads are `unknown[]` on the wire; narrowed here rather than asserted, see
      // `isTemplateTreeRow`. A row that fails the guard is one `combineArrays` would have thrown on
      // (`template.type._id`), taking the whole fetch into the catch below with it.
      const finalArray = combineArrays(
        templateTypes.data.result?.filter(isTemplateTreeRow),
        templates.data.result?.filter(isTemplateTreeRow),
      );
      setTemplateDataSource(finalArray);
    } catch (error) {
      console.error("Error fetching form template:", error);
      Promise.reject(error);
    } finally {
      setIsTemplateLoading(false);
    }
  }, [props.resourceType, props.projectId, combineArrays]);
  useEffect(() => {
    fetchTemplateDataSource();
  }, [fetchTemplateDataSource]);

  const handleFormFlowCreate = () => {
    setShowCreateFlowModal(true);
  };

  /**
   * Wired twice on purpose-by-accident: `FormFlowCreationNew` calls it with the levels it just built,
   * and the Create button's `onClick` calls it with a React mouse event. The `formFlow.length` test
   * below is what tells them apart — a mouse event has no `length`, so that branch is skipped. The
   * parameter is declared as the levels, which is the only shape read; `ButtonComponent.onClick` is
   * `(data?: any) => any`, so the button wiring still type-checks. Noted in the latent-bug backlog.
   */
  const handleCreateClick = async (formFlow: FormFlowLevel[] = []) => {
    let validatedDueDate = dueDate;
    if (validatedDueDate) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      if (new Date(validatedDueDate).getTime() < startOfToday.getTime()) {
        dispatch(
          showToastNotification({
            content: t("Due Date cannot be in the past"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        setDueDate(null);
        return;
      }
    }
    setShowLoader(true);
    let formFlowLevels;
    let formTemplateId;

    if (selectedFormFlow.formFlow) {
      formFlowLevels = selectedFormFlow.formFlow;
      // KNOWN BUG, preserved verbatim: `selectedForm` is optional on a flow row but dereferenced
      // unconditionally, so a flow stored without its template reference throws here. Erased at emit.
      formTemplateId = selectedFormFlow.selectedForm!._id;
    }
    if (formFlow.length) {
      formFlowLevels = formFlow;
      formTemplateId = selectedForm._id;
    }
    // The real request payload, so the server schema is the type. Every assertion below bridges a
    // field the schema requires against a local value that is optional or unbranded, and all of them
    // are erased at emit — an absent value still goes on the wire and is still rejected by Zod exactly
    // as it is today. KNOWN BUG, preserved verbatim: `formTemplateId` and `formFlowLevels` are only
    // assigned inside the two branches above, so reaching here with neither a selected flow nor a
    // freshly-authored one posts `selectedTemplate: undefined` and throws on `formFlowLevels.map`.
    const formData: TipfSaveRequest["data"] = {
      title,
      description,
      dueDate: validatedDueDate ?? null,
      selectedTemplate: formTemplateId!,
      rememberUserSettings,
      resourceType: selectedForm.resourceType!,
      spaceId: spaceId as ObjectIdString,
      projectId: props.projectId as ObjectIdString,
      formFlow: formFlowLevels!.map((level) => ({
        dataSource: level.dataSource,
        allowDynamicUserConfig: level.allowDynamicUserConfig,
        allowedUsersToApprove: level.allowedUsersToApprove,
        allowedUsersToRollBack: level.allowedUsersToRollBack,
        allowedUsersToReject: level.allowedUsersToReject,
        allowedUsersToSubmit: level.allowedUsersToSubmit,
        heading: level.heading,
        approveBtnTxt: level.approveBtnTxt,
        submitBtnTxt: level.submitBtnTxt,
        rejectBtnTxt: level.rejectBtnTxt,
        rollBackBtnTxt: level.rollBackBtnTxt,
        actionStatus: false,
        actionDetails: {},
      })),
    };
    if (selectedFormFlow?._id) {
      formData.selectedFormFlow = {
        name: selectedFormFlow.name!,
        _id: selectedFormFlow._id,
      };
    }
    if (props.contractId) {
      formData.basedOnBudget = true;
      formData.basedOnTasks = true;
      formData.contractId = props.contractId as ObjectIdString;
      formData.contractVersionId = contractDetails.versionId as ObjectIdString;
      formData.parentContract = contractDetails.parentId;
      formData.allParentContracts = (contractDetails.allParents ?? []) as ObjectIdString[];
      formData.allChildContracts = (contractDetails.allChilds ?? []) as ObjectIdString[];
    }

    if (attachEntity[formCategories.progressUpdates]) {
      // Erased: `noUncheckedIndexedAccess` adds `| undefined` to every index-signature read, but the
      // `if` above already tested this exact key. Same for the inspection branch below.
      formData.attachedProgressUpdates = [...attachEntity[formCategories.progressUpdates]!];
      formData.basedOnProgressUpdates = true;
      delete formData.basedOnBudget;
      delete formData.basedOnTasks;
    } else if (attachEntity[formCategories.inspection]) {
      formData.attachedInspections = [...attachEntity[formCategories.inspection]!];
      formData.basedOnInspections = true;
      delete formData.basedOnBudget;
      delete formData.basedOnTasks;
    }
    const data: TaskInspectionAndProgressFormsControllerRequest = {
      action: "save",
      data: formData,
    };
    try {
      const res = (await taskInspectionAndProgressFormsController(data)) as { data: SaveFormResponseData };
      if (res.data.status) {
        if (!props.mobile) {
          handleCancelFormAttachment();
          const urlObj = {
            resourceId: res.data.id,
            projectId: props.projectId,
            resourceType: selectedForm.resourceType,
            contractId: props.contractId,
          };
          const categoryObj = customModules.find((module) => module.resourceType == selectedForm.resourceType);
          // Erased: the map is keyed by string, and an absent `resourceType` indexes it with the
          // literal "undefined", missing and falling through to the `|| ""` exactly as before.
          const formHeader =
            categoryObj?.formHeader || formCategoryObject[selectedForm.resourceType!]?.formHeader || "";
          dispatch(
            showToastNotification({
              content: t("{{formHeader}} created successfully", { formHeader: formHeader }) as TranslatedString,
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          const location = getInspectionUrl(urlObj);
          if (selectedForm.resourceType == formCategories.changeOrder && props.contractId) {
            // KNOWN BUG, preserved verbatim: `getContractDetails`, `navigate`, `createInspectionRef`
            // and `router` are all optional props (web supplies the first two, mobile the last two),
            // but each is called unconditionally on its own path — so a caller that omits one throws
            // there. All four assertions are erased at emit; the fix is to split the props into a
            // web variant and a mobile variant.
            dispatch(props.getContractDetails!({ contractId: props.contractId as ObjectIdString }));
          }
          props.navigate!(location);
        } else {
          props.createInspectionRef!.current!.dismiss();
          props.router!.push("Forms", {
            projectId: props.projectId,
            resourceId: res.data.id,
          });
        }
      } else {
        dispatch(
          showToastNotification({
            // Server-authored message: branded with a cast (erased at emit) rather than routed through
            // `untranslated()`, which would add a call this typing pass must not emit.
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } finally {
      setShowLoader(false);
    }
  };

  function handleSelectTemplate(params: TemplateTreeRow | null) {
    if (params) {
      setSelectedForm(params);
      setTitle(`${params.name} - ${(params?.project?.noOfFormsOfThisTemplate ?? 0) + 1}`);
    } else {
      setSelectedForm({});
      setTitle("");
    }
  }

  function handleSetTitle(value: string) {
    setTitle(value.substring(0, 500));
  }
  function handleSetDescription(value: string) {
    setDescription(value.substring(0, 500));
  }

  const checkUserPartInFlow = (formFlow: FormFlowLevel[] | undefined, filter: "anyLevel" | "firstLevel") => {
    const levels: FormFlowLevel[] = Array.isArray(formFlow) ? formFlow : [];

    // Takes `| undefined` because `levels[0]` is possibly-absent under `noUncheckedIndexedAccess`, and
    // because the body already reads every member through `?.`.
    const isUserInLevel = (level: FormFlowLevel | undefined) => {
      const userIds = [
        ...(level?.allowedUsersToSubmit || []),
        ...(level?.allowedUsersToReject || []),
        ...(level?.allowedUsersToRollBack || []),
        ...(level?.allowedUsersToApprove || []),
        ...(level?.dataSource || []).flatMap((source: FormFlowStep) => source?.users || []),
      ].map((user) => user?.id);
      // The flow-user ids are branded 24-hex strings; the session user's `_id` is the same value with
      // the brand not attached on `RootState`. Erased at emit.
      return userIds.includes(userDetails._id as ObjectIdString);
    };

    if (filter === "firstLevel") {
      return isUserInLevel(levels[0]);
    }
    return levels.some(isUserInLevel);
  };

  const handleClearFlowFilter = () => {
    setFormFlowFilter("");
    setFormFlowOptions((prev) => ({
      allOptions: prev.allOptions,
      filteredOptions: prev.allOptions,
    }));
  };

  const handleFlowFilterChange = (filter: "anyLevel" | "firstLevel") => {
    // The level chips toggle: re-picking the active one clears the filter, which is the only way back
    // to the unfiltered list — the Clear filter button is reachable only when the filter matches nothing.
    if (formFlowFilter === filter) {
      handleClearFlowFilter();
      return;
    }
    setFormFlowFilter(filter);
    setFormFlowOptions((prev) => {
      const newOptions = prev.allOptions.filter((option) => {
        return checkUserPartInFlow(option.formFlow, filter);
      });
      return {
        allOptions: prev.allOptions,
        filteredOptions: newOptions,
      };
    });
  };

  return {
    title,
    setTitle,
    description,
    setDescription,
    setDueDate,
    dueDate,
    selectedFormTypeId,
    setSelectedFormTypeId,
    selectedForm,
    setSelectedForm,
    spaceId,
    setSpaceId,
    selectedFormFlow,
    setSelectedFormFlow,
    handleCancelFormAttachment,
    handleFormFlowCreate,
    attachEntity,
    setAttachEntity,
    handleCreateClick,
    progressUpdateEntityCondition,
    inspectionEntityCondition,
    isRustomjeePortal,
    isMPMRCLPortal,
    setRememberUserSettings,
    rememberUserSettings,
    allowedAttachmentConstants,
    templateDataSource,
    setShowPreview,
    showPreview,
    showCreateFlowModal,
    setShowCreateFlowModal,
    formFlowOptions,
    isTemplateLoading,
    isFormFlowLoading,
    handleSelectTemplate,
    handleSetDescription,
    handleSetTitle,
    showLoader,
    formFlowFilter,
    handleFlowFilterChange,
    handleClearFlowFilter,
  };
};
export default useCreateInspection;
